import { DemoBanner } from './DemoBanner'
import { useEffect, useRef, useState } from 'react'
import { Map as MapLibreMap } from 'maplibre-gl'
import { baseStyle } from './LiveMap'
import {
  badgeUrl, branding as fetchBranding, PRODUCT_BRANDING,
  type Branding, type Denial, type MapBase, type StorageView,
} from './api'
import type { OidcSettings } from './auth'
import { denialText, translate, type Lang } from './i18n'
import { initials } from './theme'

/**
 * La pantalla de ingreso, con el diseño de PROTECT y la marca de la agencia (§16.3d).
 *
 * Sigue el diseño de Jesús (`UI Look.jpeg`): la ciudad de la agencia a la izquierda, la marca y el
 * ingreso a la derecha. Lo que cambia de un departamento a otro —nombre, lema, escudo, colores— lo
 * trae el servidor. La contraseña y el segundo factor los pide el proveedor de identidad de la
 * agencia en su propia página: PROTECT nunca los ve (decisión del 23 de septiembre).
 */
export function SignIn({
  lang, agency, busy, problem, provider, storage, mapBase, onProviderSignIn,
  onToggleTheme, onToggleLang, unreachable = false, known = true,
}: {
  lang: Lang
  /**
   * La agencia de esta dirección (su subdominio), o nula en la dirección general, que muestra
   * PROTECT. No es la que recuerde el navegador: la general sirve a todas (decisión del 28-sep).
   */
  agency: { tenantId: string; agencyId: string } | null
  busy: boolean
  problem: Denial | null
  provider: OidcSettings | null
  /** El servidor no contestó: no se sabe si hay proveedor (H-58). */
  unreachable?: boolean
  /** Si ya se sabe cómo se ingresa (el servidor contestó al menos una vez). */
  known?: boolean
  storage: StorageView
  mapBase: MapBase
  onProviderSignIn: () => void
  onToggleTheme: () => void
  onToggleLang: () => void
}) {
  const [marca, setMarca] = useState<Branding>(PRODUCT_BRANDING)
  useEffect(() => {
    let vigente = true
    if (!agency) { setMarca(PRODUCT_BRANDING); return }
    void fetchBranding(agency.tenantId, agency.agencyId).then((b) => { if (vigente) setMarca(b) })
    return () => { vigente = false }
    // Cuando el servidor vuelve a contestar, se pide de nuevo: la de fábrica era por no saber.
  }, [agency?.tenantId, agency?.agencyId, unreachable])

  const seguro = window.location.protocol === 'https:'
  const estilo = {
    '--brand-bg': marca.primaryColor,
    '--brand-accent': marca.accentColor,
  } as React.CSSProperties

  return (
    <div className="signin-screen" style={estilo}>
      <CityPanel lang={lang} marca={marca} mapBase={mapBase} />

      <section className="signin-access" aria-labelledby="signin-title">
        <div className="signin-tools">
          <button onClick={onToggleLang}>{lang === 'en' ? 'ES' : 'EN'}</button>
          <button onClick={onToggleTheme} aria-label={translate(lang, 'signin.theme')}>☾</button>
        </div>

        <AgencyBadge name={marca.agencyName} src={badgeOf(marca)} />
        {marca.agencyName && <div className="signin-agency">{marca.agencyName}</div>}
        {marca.motto && <div className="signin-motto">{marca.motto}</div>}
        <DemoBanner notice={marca.demoNotice} />

        <h2 id="signin-title" className="signin-title">{translate(lang, 'signin.authorized')}</h2>
        <p className="signin-help">{translate(lang, 'signin.help')}</p>

        {provider ? (
          <button className="signin-primary" disabled={busy} onClick={onProviderSignIn}>
            {translate(lang, 'signin.sso')} <span aria-hidden="true">→</span>
          </button>
        ) : (
          <p className="signin-note" role={unreachable ? 'status' : undefined}>
            {translate(lang, unreachable ? 'signin.unreachable' : known ? 'app.noProvider' : 'signin.checking')}
          </p>
        )}
        <p className="signin-secure-access">{translate(lang, 'signin.secureAccess')}</p>

        {problem && <p className="error">{denialText(lang, problem)}</p>}
        {problem?.code === 'agency.other_host' && /^[a-z0-9.-]+$/i.test(problem.args?.host ?? '') && (
          <p className="signin-other-host">
            <a href={`https://${problem.args!.host}/`}>{translate(lang, 'signin.goToOwnHost', { host: problem.args!.host! })}</a>
          </p>
        )}

        {/* Solo lo que es cierto: la conexión segura si es https, el cifrado si se declaró. */}
        {(seguro || storage.encrypted) && (
          <p className="signin-security">
            <span aria-hidden="true">🔒</span>{' '}
            {[seguro ? translate(lang, 'signin.https') : null, storage.encrypted ? translate(lang, 'signin.encrypted') : null]
              .filter(Boolean).join(' · ')}
          </p>
        )}

        <footer className="signin-legal">
          <div>{translate(lang, 'signin.copyright', [new Date().getFullYear()])}</div>
          <div>{translate(lang, 'signin.license')}</div>
        </footer>
      </section>
    </div>
  )
}

/** La ciudad de la agencia, con la marca de PROTECT encima. Sin interacción: es el fondo. */
function CityPanel({ lang, marca, mapBase }: { lang: Lang; marca: Branding; mapBase: MapBase }) {
  const host = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!host.current || !marca.center) return
    const mapa = new MapLibreMap({
      container: host.current,
      // Con el mapa propio, su estilo oscuro. Si no, los mosaicos raster invertidos.
      style: mapBase.styleUrl ? baseStyle(true, mapBase) : {
        version: 8,
        sources: { base: { type: 'raster', tiles: [mapBase.tileUrl], tileSize: 256, maxzoom: mapBase.maxZoom, attribution: mapBase.attribution } },
        // Invertido y sin color: fondo oscuro con las calles claras, como en el diseño. El tono lo
        // pone la agencia encima (--brand-accent), así cualquier mosaico sirve.
        layers: [{
          id: 'base', type: 'raster', source: 'base',
          paint: { 'raster-saturation': -1, 'raster-brightness-min': 0.9, 'raster-brightness-max': 0.02, 'raster-contrast': 0.35 },
        }],
      },
      center: [marca.center.longitude, marca.center.latitude],
      zoom: 10,
      interactive: false,
      attributionControl: { compact: true },
    })
    if (marca.bounds) mapa.fitBounds([[marca.bounds.west, marca.bounds.south], [marca.bounds.east, marca.bounds.north]], { padding: 40, animate: false })
    return () => mapa.remove()
  }, [marca.center, marca.bounds, mapBase])

  return (
    <section className="signin-city" aria-hidden="true">
      <div ref={host} className="signin-map" />
      <div className="signin-tint" />
      <div className="signin-pillars">{translate(lang, 'signin.pillars')}</div>
      <div className="signin-brand">
        <Wordmark />
        {/* Las letras del logotipo oficial de i4DS, en el acento de la agencia como en el diseño. */}
        <div className="signin-by">by <span className="signin-i4ds" role="img" aria-label="i4DS" /><sup>™</sup></div>
        <div className="signin-tagline">{translate(lang, 'signin.tagline')}</div>
      </div>
      {marca.center && (
        <div className="signin-coords">
          {marca.agencyName && <div>{marca.agencyName}</div>}
          <div>{Math.abs(marca.center.latitude).toFixed(4)}° {marca.center.latitude >= 0 ? 'N' : 'S'}</div>
          <div>{Math.abs(marca.center.longitude).toFixed(4)}° {marca.center.longitude >= 0 ? 'E' : 'W'}</div>
        </div>
      )}
      <div className="signin-motto-left">{translate(lang, 'signin.dataToday')}</div>
      <div className="signin-verbs">{translate(lang, 'signin.verbs')}</div>
    </section>
  )
}

/** «PROTECT» con la O como retícula, como en el diseño. */
export function Wordmark() {
  return (
    <div className="wordmark" role="img" aria-label="PROTECT">
      <span>PR</span>
      <svg viewBox="0 0 100 100" className="reticle" aria-hidden="true">
        <circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="13" />
        <circle cx="50" cy="50" r="16" fill="var(--brand-accent)" />
        <path d="M50 0v14M50 86v14M0 50h14M86 50h14" stroke="var(--brand-accent)" strokeWidth="7" />
      </svg>
      <span>TECT</span>
      <sup className="tm">™</sup>
    </div>
  )
}

/**
 * El escudo de la agencia, o uno neutro con sus iniciales. PROTECT no trae escudos de
 * departamentos reales: mostrar el de uno que no lo cargó sería hacerse pasar por él.
 */
export function AgencyBadge({ name, src, className = 'signin-badge' }: { name: string; src: string | null; className?: string }) {
  if (src) return <img className={className} src={src} alt={name} />
  return (
    <svg className={className} viewBox="0 0 120 140" role="img" aria-label={name || 'PROTECT'}>
      <path d="M60 4 L112 22 V70 C112 104 88 126 60 136 C32 126 8 104 8 70 V22 Z"
        fill="rgba(255,255,255,0.06)" stroke="var(--brand-accent)" strokeWidth="4" />
      <text x="60" y="84" textAnchor="middle" fontSize="36" fontWeight="800" fill="#fff" letterSpacing="2">{initials(name)}</text>
    </svg>
  )
}

/** Dónde está el escudo de esa marca, o nulo si no cargó uno. */
export const badgeOf = (marca: Branding) =>
  marca.hasBadge ? badgeUrl(marca.tenantId, marca.agencyId, marca.badgeVersion) : null
